import { OTP_IDLE_TIMEOUT_MS } from '../../utils/config';

const OTP_SESSION_KEY = 'iad_otp_session';
const LAST_ACTIVITY_KEY = 'iad_last_activity';

// Activity fires on every click/keypress; only persist it this often.
const ACTIVITY_WRITE_THROTTLE_MS = 1000;

function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function write(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
}

function remove(key: string): void {
  try { localStorage.removeItem(key); } catch { /* best-effort */ }
}

/** Start the idle clock for a session that was just verified by OTP. */
export function markOtpSession(now = Date.now()): void {
  write(OTP_SESSION_KEY, '1');
  write(LAST_ACTIVITY_KEY, String(now));
}

/** Forget the idle state (logout, or a normal non-OTP login). */
export function clearIdleSession(): void {
  remove(OTP_SESSION_KEY);
  remove(LAST_ACTIVITY_KEY);
}

/** True when the current session came through the OTP step. */
export function isOtpSession(): boolean {
  return read(OTP_SESSION_KEY) === '1';
}

/**
 * True when an OTP session has been idle past the timeout. Fails closed: an OTP
 * session with no readable last-activity time counts as expired.
 */
export function isIdleExpired(now = Date.now()): boolean {
  if (!isOtpSession()) return false;
  const lastActivity = parseInt(read(LAST_ACTIVITY_KEY) || '', 10);
  if (!Number.isFinite(lastActivity)) return true;
  return now - lastActivity >= OTP_IDLE_TIMEOUT_MS;
}

/**
 * Record that the user used the add-in. No-op for non-OTP sessions, and for an
 * already-expired session — activity after the timeout must not revive it.
 */
export function recordAddinActivity(now = Date.now()): void {
  if (!isOtpSession() || isIdleExpired(now)) return;
  const lastActivity = parseInt(read(LAST_ACTIVITY_KEY) || '', 10);
  if (Number.isFinite(lastActivity) && now - lastActivity < ACTIVITY_WRITE_THROTTLE_MS) return;
  write(LAST_ACTIVITY_KEY, String(now));
}

/** Milliseconds since the last recorded activity (null if unknown) — for logs. */
export function getIdleForMs(now = Date.now()): number | null {
  const lastActivity = parseInt(read(LAST_ACTIVITY_KEY) || '', 10);
  return Number.isFinite(lastActivity) ? now - lastActivity : null;
}

/** Human-readable timeout for messages, e.g. "30 minutes" or "30 seconds". */
export function describeIdleTimeout(): string {
  const seconds = Math.round(OTP_IDLE_TIMEOUT_MS / 1000);
  if (seconds % 60 !== 0) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const minutes = seconds / 60;
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}
